import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Volume2, Snowflake, Gauge, ShieldCheck, Truck, Wrench,
  ArrowRight, CheckCircle2, Cpu,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1767548117017-ba2aafe4f8a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHw0fHxiaXRjb2luJTIwbWluaW5nJTIwZmFybSUyMGRhcmt8ZW58MHx8fHwxNzgyNzU3NDM2fDA&ixlib=rb-4.1.0&q=85";

const benefits = [
  { icon: Volume2, title: "Noise Reduction", stat: "60 dB", desc: "Channelled airflow and dampened resonance bring your rig down from 82–89 dB to approx. 60 dB." },
  { icon: Snowflake, title: "Better Cooling", stat: "20%", desc: "Improved airflow lowers chip temps and unlocks up to 20% overclocking headroom." },
  { icon: Gauge, title: "More Performance", stat: "+20%", desc: "Cooler silicon means you can safely push more hashrate from the same hardware." },
];

const models = [
  "S19 Pro", "L7", "X5", "S21", "S19K Pro", "S19 XP",
  "S19j (Pro)", "S19a (Pro)", "S19", "L9", "D7 / D9", "S19 (93/95TH)",
  "KS3", "KS5 (Pro)", "AL1 (Pro)", "DR7", "K7", "KA3",
];

const Home = () => {
  const [products, setProducts] = useState([]);
  useEffect(() => { fetchProducts().then(setProducts).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="ASIC mining farm" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#09090B]/80" />
        <div className="grain absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-7xl px-5 py-28 lg:px-8 lg:py-40">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-sm border border-[#27272A] bg-[#09090B]/60 px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-primary">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Engineered for miners
            </span>
            <h1 className="mt-6 font-heading text-4xl font-semibold tracking-tighter text-[#FAFAFA] sm:text-5xl lg:text-6xl">
              Quieter. Cooler.<br />
              <span className="text-primary">Faster mining.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#A1A1AA]">
              Premium PETG cooling shrouds for Bitmain Antminer S19, S19 Pro & S21.
              Cut noise to 60dB, drop chip temps, and unlock up to 20% overclocking headroom.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/product/asic-fan-shroud" data-testid="hero-shop-cta" className="group inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C] hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]">
                Shop the Shroud <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/shop" data-testid="hero-all-products" className="inline-flex items-center gap-2 rounded-sm border border-[#27272A] px-7 py-3.5 text-sm font-semibold text-[#FAFAFA] transition-colors hover:border-primary">
                View all products
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-y border-[#27272A] bg-[#0E0E11]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-5 lg:grid-cols-4 lg:px-8">
          {[
            { icon: ShieldCheck, t: "Secure Payment", s: "Stripe encrypted checkout" },
            { icon: Truck, t: "Fast Shipping", s: "Ships within 48 hours" },
            { icon: Wrench, t: "Easy Install", s: "Under 5 minutes, no tools" },
            { icon: CheckCircle2, t: "12-Mo Warranty", s: "Quality guaranteed" },
          ].map((x, i) => (
            <div key={i} className="flex items-center gap-3 py-6">
              <x.icon size={22} className="text-primary" />
              <div>
                <div className="text-sm font-medium text-[#FAFAFA]">{x.t}</div>
                <div className="text-xs text-[#A1A1AA]">{x.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Shop</span>
            <h2 className="mt-3 font-heading text-2xl font-medium tracking-tight text-[#FAFAFA] sm:text-3xl">
              Shop the shrouds
            </h2>
          </div>
          <Link to="/shop" data-testid="featured-view-all" className="hidden items-center gap-1 text-sm text-primary hover:underline sm:flex">
            View all <ArrowRight size={15} />
          </Link>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* BENEFITS BENTO */}
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <div className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Why AsicMod</span>
          <h2 className="mt-3 font-heading text-2xl font-medium tracking-tight text-[#FAFAFA] sm:text-3xl lg:text-4xl">
            Three measurable upgrades from one part.
          </h2>
        </div>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`benefit-${i}`}
              className="rounded-sm border border-[#27272A] bg-[#0E0E11] p-8 transition-colors hover:border-[#EA580C]"
            >
              <b.icon size={28} className="text-primary" />
              <div className="mt-6 font-mono text-4xl font-semibold text-[#FAFAFA]">{b.stat}</div>
              <h3 className="mt-2 font-heading text-xl font-medium text-[#FAFAFA]">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#A1A1AA]">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMPATIBLE MODELS */}
      <section className="border-t border-[#27272A] bg-[#0E0E11]">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Compatibility</span>
          <h2 className="mt-3 font-heading text-2xl font-medium tracking-tight text-[#FAFAFA] sm:text-3xl">
            Compatible models
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-[#27272A] bg-[#27272A] sm:grid-cols-3 lg:grid-cols-6">
            {models.map((m) => (
              <div key={m} data-testid={`model-${m}`} className="flex items-center gap-3 bg-[#0E0E11] p-5">
                <Cpu size={16} className="shrink-0 text-primary" />
                <span className="font-mono text-sm text-[#FAFAFA]">{m}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
